import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PublicPageLayout from './PublicPageLayout';
import { countries, commonCurrencyCodes, getCurrencyForCountry } from '../utils/countriesStates';
import PasswordHint from './PasswordHint';
import { passwordProblem } from '../utils/passwordPolicy';
import { savedReferral, forgetReferral } from '../utils/referral';

function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    organizationName: '',
    country: '',
    currency: '',
    referralCode: savedReferral()
  });
  // The "Referred by" box shows only while the affiliate program is on (or a code came with the link)
  const [referralOpen, setReferralOpen] = useState(Boolean(savedReferral()));
  const [referrer, setReferrer] = useState(null); // { valid, name } for the code typed
  const [currencyTouched, setCurrencyTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/public/referral/status').then((r) => { if (r.data?.enabled) setReferralOpen(true); }).catch(() => {});
  }, []);

  const lookUpReferral = async (code) => {
    const c = (code || '').trim();
    if (!c) { setReferrer(null); return; }
    try {
      const { data } = await axios.get(`/api/public/referral/${encodeURIComponent(c)}`);
      setReferrer(data);
    } catch (e) {
      setReferrer(null);
    }
  };

  useEffect(() => {
    if (formData.referralCode) lookUpReferral(formData.referralCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    const countryObj = countries.find((c) => c.code === countryCode);
    const countryName = countryObj ? countryObj.name : '';
    const suggestedCurrency = getCurrencyForCountry(countryCode);
    // Only auto-fill when the suggestion is actually one of the offered options - otherwise the
    // select would silently hold a value with no matching <option>, showing as blank.
    const suggestionIsSelectable = commonCurrencyCodes.some((c) => c.code === suggestedCurrency);
    setFormData((prev) => ({
      ...prev,
      country: countryName,
      // Suggest a currency for the newly-picked country, but never override a choice the user
      // already made themselves.
      currency: currencyTouched ? prev.currency : (suggestionIsSelectable ? suggestedCurrency : prev.currency)
    }));
  };

  const handleCurrencyChange = (e) => {
    setCurrencyTouched(true);
    setFormData({ ...formData, currency: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const weak = passwordProblem(formData.password);
    if (weak) {
      setError(weak);
      return;
    }
    setLoading(true);

    const result = await register({ ...formData, referralCode: formData.referralCode.trim() || undefined });

    if (result.success) {
      forgetReferral();
      toast.success('Registration successful! Check your email for a verification code.');
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
    } else if (result.status === 503) {
      // Account was created - only the verification email itself failed to send (email service
      // issue). Still get them to the code-entry screen; they can use "Resend" once mail is back.
      toast.error(result.error);
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
    } else {
      setError(result.error);
      toast.error('Registration failed');
    }

    setLoading(false);
  };

  return (
    <PublicPageLayout subtitle="Create your account" colProps={{ md: 8, lg: 6 }}>
      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                placeholder="Enter first name"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                placeholder="Enter last name"
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="Enter email"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Password</Form.Label>
          <Form.Control
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            placeholder="Enter password"
            autoComplete="new-password"
          />
          <PasswordHint password={formData.password} />
        </Form.Group>

        <hr />

        <h5>Organization Details</h5>

        <Form.Group className="mb-3">
          <Form.Label>Organization Name</Form.Label>
          <Form.Control
            type="text"
            name="organizationName"
            value={formData.organizationName}
            onChange={handleChange}
            required
            placeholder="Enter organization name"
          />
          <Form.Text className="text-muted">
            A new organization will be created for you
          </Form.Text>
        </Form.Group>

        {referralOpen && (
          <Form.Group className="mb-3">
            <Form.Label>Referred by (optional)</Form.Label>
            <Form.Control
              type="text"
              name="referralCode"
              value={formData.referralCode}
              onChange={(e) => { handleChange(e); setReferrer(null); }}
              onBlur={(e) => lookUpReferral(e.target.value)}
              placeholder="Your accountant's or adviser's code"
              maxLength={12}
              data-testid="referral-code"
            />
            {referrer?.valid && <Form.Text className="text-success">Referred by {referrer.name}</Form.Text>}
            {referrer && !referrer.valid && <Form.Text className="text-danger">That code isn&apos;t recognised. Check it, or leave it empty.</Form.Text>}
            {!referrer && <Form.Text className="text-muted">If an accountant or adviser suggested iBigDo, type the code they gave you.</Form.Text>}
          </Form.Group>
        )}

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Country</Form.Label>
              <Form.Select
                value={countries.find((c) => c.name === formData.country)?.code || ''}
                onChange={handleCountryChange}
                required
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>{country.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Default Currency</Form.Label>
              <Form.Select
                value={formData.currency}
                onChange={handleCurrencyChange}
                required
              >
                <option value="">Select Currency</option>
                {commonCurrencyCodes.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Button
          variant="primary"
          type="submit"
          className="w-100"
          disabled={loading}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </Button>
        <p className="text-muted small text-center mt-2 mb-0">
          By creating an account you agree to the <Link to="/terms">Terms of Service</Link> and
          the <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </Form>

      <div className="text-center mt-3">
        <a href="/guide/getting-started.html" target="_blank" rel="noopener noreferrer">
          <i className="bi bi-book me-1" />
          New to iBigDo? Read the Getting Started guide
        </a>
      </div>
    </PublicPageLayout>
  );
}

export default Register;
